// src/components/quality/CriteriaItem.js
import { useState, useEffect } from 'react';
import api from '../../services/api';

const CriteriaItem = ({ criterion, onUpdate }) => {
  const [newOption, setNewOption] = useState({ text: '', score: 1.0, order: 1 });
  const [weight, setWeight] = useState(criterion.weight);
  const [isCritical, setIsCritical] = useState(criterion.critical);
  const [isPositive, setIsPositive] = useState(criterion.positive);
  const [options, setOptions] = useState(criterion.options);
  const [isExpanded, setIsExpanded] = useState(false);
  const [typeSkills, setTypeSkills] = useState(criterion.type_skills || 'soft');
  
  // Состояние для управления зависимостями
  const [dependencyField, setDependencyField] = useState('');
  const [dependencyValues, setDependencyValues] = useState('');
  const [isEditingDependency, setIsEditingDependency] = useState(false);
  const [editError, setEditError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [defaultOptionId, setDefaultOptionId] = useState(null);
  
  // Инициализация зависимостей и дефолтного значения при монтировании
  useEffect(() => {
    if (criterion.dependencies && criterion.dependencies.length > 0) {
      const dep = criterion.dependencies[0]; // Берём первую зависимость (все одного поля)
      setDependencyField(dep.field_name);
      // Собираем все значения для этого поля
      const values = criterion.dependencies.map(d => d.field_value).join(', ');
      setDependencyValues(values);
    } else {
      setDependencyField('');
      setDependencyValues('');
    }
    
    // Инициализация дефолтного значения
    setDefaultOptionId(criterion.default_option_id);
  }, [criterion.dependencies, criterion.default_option_id]);

  // Переключение типа навыков
  const handleTypeSkillsToggle = () => {
    const newValue = typeSkills === 'soft' ? 'hard' : 'soft';
    setTypeSkills(newValue);

    api.patch(`/quality/criteria/${criterion.id}/type_skills`, { type_skills: newValue })
      .catch((err) => {
        console.error('Ошибка обновления типа навыков:', err);
        setTypeSkills(criterion.type_skills || 'soft');
      });
  };

  // Редактирование comment_prompt
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState(criterion.comment_prompt || '');

  const sortedOptions = [...options].sort((a, b) => a.order - b.order);

  // Обновление веса
  const handleWeightChange = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value <= 0) return;

    setWeight(value);

    api.patch(`/quality/criteria/${criterion.id}/weight`, { weight: value })
      .catch((err) => {
        console.error('Ошибка обновления веса:', err);
        setWeight(criterion.weight);
      });
  };

  // Переключение позитивности
  const handlePositiveToggle = () => {
    const newValue = !isPositive;
    setIsPositive(newValue);

    api.patch(`/quality/criteria/${criterion.id}/positive`, { positive: newValue })
      .catch((err) => {
        console.error('Ошибка обновления позитивности:', err);
        setIsPositive(criterion.positive);
      });
  };

  // Переключение критичности
  const handleCriticalToggle = () => {
    const newValue = !isCritical;
    setIsCritical(newValue);

    api.patch(`/quality/criteria/${criterion.id}/critical`, { critical: newValue })
      .catch((err) => {
        console.error('Ошибка обновления флага critical:', err);
        setIsCritical(criterion.critical);
      });
  };

  // Редактирование comment_prompt
  const handleEditComment = () => {
    setIsEditingComment(true);
    setCommentText(criterion.comment_prompt || '');
  };

  const handleSaveComment = () => {
    setIsEditingComment(false);

    api.patch(`/quality/criteria/${criterion.id}/comment_prompt`, {
      comment_prompt: commentText.trim() || null,
    })
      .catch((err) => {
        console.error('Ошибка сохранения подсказки:', err);
        setCommentText(criterion.comment_prompt || '');
      });
  };

  // Добавление варианта
  const handleAddOption = (e) => {
    e.preventDefault();
    if (!newOption.text.trim()) {
      window.showToast?.('Введите текст варианта');
      return;
    }
    const score = parseFloat(newOption.score);
    if (isNaN(score)) {
      window.showToast?.('Некорректный балл');
      return;
    }

    const maxOrder = Math.max(...options.map((o) => o.order), 0);
    const newOpt = { text: newOption.text, score, order: maxOrder + 1 };

    api.post(`/quality/criteria/${criterion.id}/options`, [newOpt])
      .then((res) => {
        const updatedOptions = [...options, res.data.data[0]]; // ← res.data.data[0]
        setOptions(updatedOptions);
        onUpdate(criterion.id, updatedOptions);
        setNewOption({ text: '', score: 1.0, order: 1 });
      })
      .catch((err) => {
        console.error('Ошибка добавления варианта:', err);
        window.showToast?.('Не удалось добавить вариант');
      });
  };

  // Удаление варианта
  const handleRemoveOption = (optionId) => {
    if (!window.confirm('Удалить этот вариант ответа?')) return;

    api.delete(`/quality/criteria/options/${optionId}`)
      .then(() => {
        const updatedOptions = options.filter((opt) => opt.id !== optionId);
        setOptions(updatedOptions);
        onUpdate(criterion.id, updatedOptions);
      })
      .catch((err) => {
        console.error('Ошибка удаления варианта:', err);
        window.showToast?.('Не удалось удалить вариант');
      });
  };

  // Перемещение варианта
  const moveOption = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sortedOptions.length - 1)
    )
      return;

    const newOptions = [...sortedOptions];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newOptions[index].order;
    newOptions[index].order = newOptions[swapIndex].order;
    newOptions[swapIndex].order = temp;

    setOptions(newOptions);

    Promise.all([
      api.patch(`/quality/criteria/options/${newOptions[index].id}/order`, {
        order: newOptions[index].order,
      }),
      api.patch(`/quality/criteria/options/${newOptions[swapIndex].id}/order`, {
        order: newOptions[swapIndex].order,
      }),
    ])
      .catch((err) => {
        console.error('Ошибка обновления порядка:', err);
        setOptions(options);
      });
  };

  // Обработчик сохранения зависимости
  const handleSaveDependency = async () => {
    setEditError('');
    setSubmitting(true);
    
    // Валидация
    if (dependencyField && (!dependencyValues || dependencyValues.trim() === '')) {
      setEditError('При выборе поля необходимо указать хотя бы одно значение');
      setSubmitting(false);
      return;
    }
    
    try {
      const payload = dependencyField 
        ? dependencyValues
            .split(',')
            .map(v => v.trim())
            .filter(v => v)
            .map(value => ({
              field_name: dependencyField,
              field_value: value
            }))
        : [];
      
      await api.post(`/quality/criteria/${criterion.id}/dependencies`, payload);
      
      // Сохраняем дефолтное значение
      if (defaultOptionId !== null) {
        await api.patch(`/quality/criteria/${criterion.id}/default_option`, {
          default_option_id: defaultOptionId
        });
      }

      // window.showToast?.('Зависимость и дефолтное значение сохранены');
      setIsEditingDependency(false);
      
      // Обновляем отображение
      if (payload.length > 0) {
        setDependencyField(payload[0].field_name);
        setDependencyValues(payload.map(p => p.field_value).join(', '));
      } else {
        setDependencyField('');
        setDependencyValues('');
      }
    } catch (err) {
      console.error('Ошибка сохранения зависимости:', err);
      setEditError(err.response?.data?.error || 'Не удалось сохранить зависимость');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Обработчик отмены редактирования
  const handleCancelEdit = () => {
    setIsEditingDependency(false);
    setEditError('');
    // Сбрасываем изменения
    if (criterion.dependencies && criterion.dependencies.length > 0) {
      const dep = criterion.dependencies[0];
      setDependencyField(dep.field_name);
      const values = criterion.dependencies.map(d => d.field_value).join(', ');
      setDependencyValues(values);
    } else {
      setDependencyField('');
      setDependencyValues('');
    }
  };
  
  return (
    <div className="criteria-card">
      <div className="card-header">
        <h4 className="card-title">
          <button
            type="button"
            className="expand-toggle"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            title={isExpanded ? 'Свернуть' : 'Развернуть'}
          >
            {isExpanded ? '▼' : '▶'} {criterion.name}
          </button>
        </h4>
        
        {/* Блок редактирования зависимостей */}
        {isExpanded && isEditingDependency && (
          <div className="card-info" style={{ marginTop: '10px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <h5 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Зависимость критерия</h5>
            
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label>Поле, от которого зависит критерий</label>
              <select
                value={dependencyField}
                onChange={(e) => {
                  setDependencyField(e.target.value);
                  // При смене поля сбрасываем значения
                  setDependencyValues('');
                  setEditError('');
                }}
                className="project-select"
              >
                <option value="">-- Не зависит от поля --</option>
                <option value="impact">Impact</option>
                <option value="assessment_type">Тип оценки</option>
                <option value="srq">SRQ</option>
                <option value="status">Статус</option>
              </select>
            </div>

            {dependencyField && (
              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label>Значения поля (через запятую) *</label>
                <input
                  type="text"
                  value={dependencyValues}
                  onChange={(e) => setDependencyValues(e.target.value)}
                  placeholder="Например: High,Medium"
                  className="project-select"
                  required
                />
              </div>
            )}
            

            
            {editError && <div className="form-error" style={{ marginBottom: '10px' }}>{editError}</div>}
            
            <div className="modal-actions" style={{ justifyContent: 'flex-start', gap: '10px' }}>
              <button
                type="button"
                className="btn-submit"
                onClick={handleSaveDependency}
                disabled={submitting}
              >
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                Отмена
              </button>
            </div>
          </div>
        )}
        <div className="card-tags">
          <button
            type="button"
            className={`tag ${isPositive ? 'positive' : 'negative'} toggle-tag`}
            onClick={handlePositiveToggle}
            title="Переключить тип критерия"
          >
            {isPositive ? 'Позитивный' : 'Негативный'}
          </button>
          <button
            type="button"
            className={`tag ${isCritical ? 'danger' : 'secondary'} toggle-tag`}
            onClick={handleCriticalToggle}
            title="Переключить критичность"
          >
            {isCritical ? 'КРИТИЧНО' : 'обычный'}
          </button>
          <button
            type="button"
            className={`tag ${typeSkills === 'hard' ? 'info' : 'warning'} toggle-tag`}
            onClick={handleTypeSkillsToggle}
            title="Переключить тип навыков"
          >
            {typeSkills === 'soft' ? 'Soft' : 'Hard'}
          </button>
          <button
            type="button"
            className="tag danger toggle-tag"
            onClick={async () => {
              if (!window.confirm('Вы уверены, что хотите удалить этот критерий? Это действие нельзя отменить.')) return;
              
              try {
                await api.delete(`/quality/criteria/${criterion.id}`);
                window.showToast?.('Критерий успешно удалён');
                // Удаляем критерий из родительского состояния
                onUpdate(criterion.id, null);
              } catch (err) {
                console.error('Ошибка удаления критерия:', err);
                window.showToast?.('Не удалось удалить критерий');
              }
            }}
            title="Удалить критерий"
          >
            Удалить
          </button>
        </div>
      </div>

      {/* Условный рендер содержимого */}
      {isExpanded && (
        <div className="card-body">
          <div className="card-info">
            <span className="info-item">
              Вес:
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={weight}
                onChange={handleWeightChange}
                className="weight-input"
                style={{ width: '60px', marginLeft: '6px' }}
              />
            </span>
            
            {/* Отображение зависимости */}
            {(isExpanded && (criterion.dependencies === undefined || criterion.dependencies.length === 0 || dependencyField || isEditingDependency)) && (
              <div className="info-item" style={{ marginTop: '10px', display: 'block' }}>
                {dependencyField ? (
                  <>
                    <strong>Зависит от:</strong> {dependencyField} = {dependencyValues || '—'}
                    <button
                      type="button"
                      onClick={() => setIsEditingDependency(true)}
                      className="btn-info"
                      style={{ marginLeft: '10px', fontSize: '12px' }}
                    >
                      ✏️ Изменить
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditingDependency(true)}
                    className="btn-info"
                    style={{ fontSize: '14px' }}
                  >
                    ➕ Добавить зависимость
                  </button>
                )}
              </div>
            )}

            {/* Редактируемый comment_prompt — теперь с textarea */}
            <div className="info-item comment-edit-section">
              <span className="label">Подсказка:</span>
              {isEditingComment ? (
                <div className="edit-comment-form">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Введите подсказку для комментария (например: Укажите, что именно было не так)"
                    autoFocus
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      marginBottom: '8px',
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={handleSaveComment}
                      title="Сохранить"
                    >
                      ✓ Сохранить
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setIsEditingComment(false);
                        setCommentText(criterion.comment_prompt || '');
                      }}
                      title="Отмена"
                    >
                      ✕ Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <div className="comment-display">
                  <div
                    className="comment-text"
                    title={commentText || 'Нет подсказки'}
                    style={{
                      fontStyle: commentText ? 'normal' : 'italic',
                      color: commentText ? 'inherit' : '#888',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9',
                      border: '1px dashed #ccc',
                      fontSize: '14px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={handleEditComment}
                  >
                    {commentText ? (
                      <span>💬 {commentText}</span>
                    ) : (
                      <span>Кликните, чтобы добавить подсказку для комментария</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-info"
                    onClick={handleEditComment}
                    title="Редактировать подсказку"
                    style={{ marginTop: '6px' }}
                  >
                    ✏️ Редактировать
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="options-section">
            <div className="options-header">
              <h5>Варианты ответов</h5>
              <label className="default-radio" style={{ marginLeft: '20px' }}>
                <input
                  type="radio"
                  name="default_option"
                  checked={defaultOptionId === null}
                  onChange={async () => {
                    setDefaultOptionId(null);
                    try {
                      await api.patch(`/quality/criteria/${criterion.id}/default_option`, {
                        default_option_id: null
                      });
                      window.showToast?.('Дефолтный вариант сброшен');
                    } catch (err) {
                      console.error('Ошибка сброса дефолтного значения:', err);
                      window.showToast?.('Не удалось сбросить дефолтный вариант');
                    }
                  }}
                />
                Без дефолтного
              </label>
            </div>
            <ul className="options-list">
              {sortedOptions.map((opt, index) => (
                <li key={opt.id} className="option-item">
                  <span>
                    "{opt.text}" — {opt.score} балл(а), порядок: {opt.order}
                  </span>
                  <div className="option-actions">
                    <button
                      type="button"
                      onClick={() => moveOption(index, 'up')}
                      disabled={index === 0}
                      className="btn btn-sm btn-secondary"
                      title="Вверх"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveOption(index, 'down')}
                      disabled={index === sortedOptions.length - 1}
                      className="btn btn-sm btn-secondary"
                      title="Вниз"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => handleRemoveOption(opt.id)}
                      className="btn-danger"
                      title="Удалить вариант"
                    >
                      ×
                    </button>
                    <label className="default-radio">
                      <input
                        type="radio"
                        name="default_option"
                        checked={defaultOptionId === opt.id}
                        onChange={async () => {
                          setDefaultOptionId(opt.id);
                          try {
                            await api.patch(`/quality/criteria/${criterion.id}/default_option`, {
                              default_option_id: opt.id
                            });
                            // window.showToast?.('Дефолтный вариант обновлён');
                          } catch (err) {
                            console.error('Ошибка сохранения дефолтного значения:', err);
                            window.showToast?.('Не удалось сохранить дефолтный вариант');
                          }
                        }}
                      />
                      Дефолт
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleAddOption} className="add-option-form">
            <input
              type="text"
              placeholder="Текст варианта"
              value={newOption.text}
              onChange={(e) => setNewOption({ ...newOption, text: e.target.value })}
              required
            />
            <input
              type="number"
              step="0.1"
              placeholder="Балл"
              value={newOption.score}
              onChange={(e) =>
                setNewOption({
                  ...newOption,
                  score: parseFloat(e.target.value) || 1.0,
                })
              }
              required
            />
            <button type="submit" className="btn-add">Добавить</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CriteriaItem;
